import React, { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { FindNearestStore } from "../../../requests";

// Default coordinates — Ranchi city centre
const DEFAULT_LAT = "23.3441";
const DEFAULT_LNG = "85.3096";

const REASON_LABELS = {
  INACTIVE: { label: "Inactive", color: "secondary" },
  CLOSED: { label: "Closed", color: "warning" },
  OUTSIDE_RADIUS: { label: "Outside Radius", color: "danger" },
  MISSING_COORDINATES: { label: "No GPS Data", color: "dark" },
};

function RejectionBadge({ reason }) {
  const meta = REASON_LABELS[reason] || { label: reason, color: "secondary" };
  return <Badge color={meta.color}>{meta.label}</Badge>;
}

function formatDistance(d) {
  if (d == null) return "—";
  return `${d} km`;
}

export default function LocationPlayground() {
  const role = useSelector((state) => state?.login?.role);

  const [lat, setLat] = useState(DEFAULT_LAT);
  const [lng, setLng] = useState(DEFAULT_LNG);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showRaw, setShowRaw] = useState(false);

  // Wait for role to be hydrated from Redux before redirecting
  if (role === null || role === undefined) return null;
  if (role !== "superadmin") return <Navigate to="/dashboard" />;

  const handleFindStore = async () => {
    console.log("handleFindStore: button clicked!");
    setError(null);
    setResult(null);
    setShowRaw(false);

    const latitude = parseFloat(lat);
    const longitude = parseFloat(lng);

    console.log("handleFindStore parsed coordinates:", latitude, longitude);

    if (isNaN(latitude) || isNaN(longitude)) {
      setError("Please enter valid numeric latitude and longitude values.");
      return;
    }
    if (latitude < -90 || latitude > 90) {
      setError("Latitude must be between -90 and 90.");
      return;
    }
    if (longitude < -180 || longitude > 180) {
      setError("Longitude must be between -180 and 180.");
      return;
    }

    try {
      setLoading(true);
      console.log("handleFindStore: calling FindNearestStore...");
      const response = await FindNearestStore(latitude, longitude);
      console.log("handleFindStore: API response received:", response);
      const data = response && response.data !== undefined ? response.data : response;
      setResult(data);
    } catch (err) {
      console.error("[LocationPlayground] findStore error:", err);
      setError(
        err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          err?.message ||
          "Unknown error calling Location Engine."
      );
    } finally {
      setLoading(false);
    }
  };

  const diagnostics = result?.diagnostics;

  // Build a unified store table from qualified + rejected
  const qualifiedEntry = result?.success
    ? {
        storeId: result.store?.id,
        name: result.store?.name,
        distance: result.distance,
        status: "QUALIFIED",
        reason: null,
        deliveryRadius: result.store?.deliveryRadius,
        isNearest: true,
      }
    : null;

  const rejectedEntries = (diagnostics?.rejectionReasons || []).map((r) => ({
    storeId: r.storeId,
    name: r.name,
    distance: r.distance,
    status: "REJECTED",
    reason: r.reason,
    deliveryRadius: r.deliveryRadius ?? null,
    openingTime: r.openingTime,
    closingTime: r.closingTime,
    isNearest: false,
  }));

  const allEntries = [
    ...(qualifiedEntry ? [qualifiedEntry] : []),
    ...rejectedEntries,
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="Developer Tools"
          breadcrumbItem="Location Playground"
        />

        {/* ── Input Card ─────────────────────────────────── */}
        <Row className="mb-4">
          <Col md={12}>
            <Card className="shadow-sm border-0">
              <CardBody>
                <div className="d-flex align-items-center mb-3 gap-2">
                  <i
                    className="mdi mdi-map-marker-radius"
                    style={{ fontSize: "1.4rem", color: "#556ee6" }}
                  />
                  <h5 className="mb-0 fw-bold">Customer GPS Coordinates</h5>
                </div>
                <p className="text-muted small mb-4">
                  Enter the customer's GPS coordinates to simulate the Location
                  Engine. The engine will find the nearest eligible store using
                  the Haversine formula and apply business rules BR-L001..L004.
                </p>

                <Row className="align-items-end">
                  <Col md={4}>
                    <FormGroup>
                      <Label for="locLat" className="fw-semibold">
                        Latitude
                      </Label>
                      <Input
                        id="locLat"
                        type="number"
                        step="0.0001"
                        placeholder="e.g. 23.3441"
                        value={lat}
                        onChange={(e) => setLat(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label for="locLng" className="fw-semibold">
                        Longitude
                      </Label>
                      <Input
                        id="locLng"
                        type="number"
                        step="0.0001"
                        placeholder="e.g. 85.3096"
                        value={lng}
                        onChange={(e) => setLng(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={4} className="mb-3">
                    <Button
                      id="btnFindStore"
                      color="primary"
                      className="w-100"
                      onClick={handleFindStore}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Calculating...
                        </>
                      ) : (
                        <>
                          <i className="mdi mdi-crosshairs-gps me-2" />
                          Find Store
                        </>
                      )}
                    </Button>
                  </Col>
                </Row>

                {error && (
                  <Alert color="danger" className="mt-2 mb-0">
                    <i className="mdi mdi-alert-circle me-2" />
                    {error}
                  </Alert>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ── Results ────────────────────────────────────── */}
        {result && (
          <>
            {/* Summary Cards */}
            <Row className="mb-4">
              {/* Nearest Store */}
              <Col md={4}>
                <Card
                  className="shadow-sm border-0 h-100"
                  style={{
                    borderLeft: result.success
                      ? "4px solid #34c38f"
                      : "4px solid #f46a6a",
                  }}
                >
                  <CardBody>
                    <h6 className="text-uppercase text-muted fw-semibold mb-3">
                      <i className="mdi mdi-store me-2" />
                      Result
                    </h6>
                    {result.success ? (
                      <>
                        <h4 className="fw-bold mb-1" style={{ color: "#34c38f" }}>
                          ✅ {result.store?.name}
                        </h4>
                        <p className="text-muted mb-1">{result.store?.address || "—"}</p>
                        <div className="d-flex gap-3 mt-2">
                          <div>
                            <div className="fw-semibold" style={{ fontSize: "1.4rem" }}>
                              {result.distance} km
                            </div>
                            <small className="text-muted">Distance</small>
                          </div>
                          <div>
                            <div className="fw-semibold" style={{ fontSize: "1.4rem" }}>
                              {result.store?.deliveryRadius ?? "—"} km
                            </div>
                            <small className="text-muted">Delivery Radius</small>
                          </div>
                        </div>
                        <p className="text-muted small mt-2 mb-0">
                          Store ID: <code>{result.store?.id}</code>
                        </p>
                      </>
                    ) : (
                      <>
                        <h4 className="fw-bold mb-1" style={{ color: "#f46a6a" }}>
                          ❌ No Store Available
                        </h4>
                        <p className="text-muted mb-0">
                          No eligible store was found for these coordinates.
                          Check that stores are active, open, and have sufficient
                          delivery radius.
                        </p>
                      </>
                    )}
                  </CardBody>
                </Card>
              </Col>

              {/* Diagnostics */}
              <Col md={4}>
                <Card className="shadow-sm border-0 h-100" style={{ borderLeft: "4px solid #f1b44c" }}>
                  <CardBody>
                    <h6 className="text-uppercase text-muted fw-semibold mb-3">
                      <i className="mdi mdi-chart-bar me-2" />
                      Diagnostics
                    </h6>
                    <Row>
                      <Col xs={4} className="text-center">
                        <div
                          className="fw-bold"
                          style={{ fontSize: "1.8rem", color: "#556ee6" }}
                        >
                          {diagnostics?.storesChecked ?? "—"}
                        </div>
                        <small className="text-muted">Checked</small>
                      </Col>
                      <Col xs={4} className="text-center">
                        <div
                          className="fw-bold"
                          style={{ fontSize: "1.8rem", color: "#34c38f" }}
                        >
                          {diagnostics?.storesQualified ?? "—"}
                        </div>
                        <small className="text-muted">Qualified</small>
                      </Col>
                      <Col xs={4} className="text-center">
                        <div
                          className="fw-bold"
                          style={{ fontSize: "1.8rem", color: "#f46a6a" }}
                        >
                          {diagnostics?.storesRejected ?? "—"}
                        </div>
                        <small className="text-muted">Rejected</small>
                      </Col>
                    </Row>
                    <hr className="my-3" />
                    <div className="d-flex align-items-center gap-2">
                      <i className="mdi mdi-timer-outline text-muted" />
                      <span className="text-muted small">
                        Execution time:{" "}
                        <strong>{diagnostics?.executionTimeMs ?? "—"} ms</strong>
                      </span>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              {/* Distance Service Diagnostics */}
              <Col md={4}>
                <Card className="shadow-sm border-0 h-100" style={{ borderLeft: "4px solid #50a5f1" }}>
                  <CardBody>
                    <h6 className="text-uppercase text-muted fw-semibold mb-3">
                      <i className="mdi mdi-map-marker-distance me-2 text-info" />
                      Distance Provider
                    </h6>
                    <div className="d-flex flex-column gap-2">
                      <div>
                        <small className="text-muted d-block">Configured Provider</small>
                        <span className="fw-bold text-info">
                          {result.distanceDiagnostics?.provider || "HAVERSINE"}
                        </span>
                      </div>
                      <div>
                        <small className="text-muted d-block">Straight-line Distance</small>
                        <span className="fw-semibold text-dark">
                          {result.distanceDiagnostics?.straightLineDistanceKm != null
                            ? `${Number(result.distanceDiagnostics.straightLineDistanceKm).toFixed(2)} km`
                            : result.distance != null ? `${Number(result.distance).toFixed(2)} km` : "—"}
                        </span>
                      </div>
                      <div>
                        <small className="text-muted d-block">Road Distance / Duration</small>
                        <span className="text-muted small">
                          {result.distanceDiagnostics?.roadDistanceKm != null
                            ? `${Number(result.distanceDiagnostics.roadDistanceKm).toFixed(2)} km`
                            : "—"}
                          {result.distanceDiagnostics?.estimatedDurationMinutes != null
                            ? ` (${result.distanceDiagnostics.estimatedDurationMinutes} mins)`
                            : " (N/A)"}
                        </span>
                      </div>
                      <div className="border-top pt-2 mt-1 d-flex align-items-center gap-2">
                        <i className="mdi mdi-speedometer text-muted" />
                        <span className="text-muted small">
                          Calculation Time:{" "}
                          <strong>
                            {result.distanceDiagnostics?.executionTimeMs != null
                              ? `${result.distanceDiagnostics.executionTimeMs} ms`
                              : "0 ms"}
                          </strong>
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Per-Store Table */}
            {allEntries.length > 0 && (
              <Row className="mb-4">
                <Col md={12}>
                  <Card className="shadow-sm border-0">
                    <CardBody>
                      <h6 className="text-uppercase text-muted fw-semibold mb-3">
                        <i className="mdi mdi-table me-2" />
                        Store Analysis
                      </h6>
                      <div className="table-responsive">
                        <Table hover className="mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Store</th>
                              <th>Distance</th>
                              <th>Delivery Radius</th>
                              <th>Status</th>
                              <th>Result</th>
                            </tr>
                          </thead>
                          <tbody>
                            {allEntries.map((entry, idx) => (
                              <tr
                                key={idx}
                                style={
                                  entry.isNearest
                                    ? { background: "rgba(52,195,143,0.08)" }
                                    : {}
                                }
                              >
                                <td>
                                  <div className="fw-semibold">
                                    {entry.isNearest && (
                                      <i
                                        className="mdi mdi-star me-1"
                                        style={{ color: "#f1b44c" }}
                                        title="Nearest eligible store"
                                      />
                                    )}
                                    {entry.name}
                                  </div>
                                  <small className="text-muted">
                                    {entry.storeId}
                                  </small>
                                </td>
                                <td>{formatDistance(entry.distance)}</td>
                                <td>
                                  {entry.deliveryRadius != null
                                    ? `${entry.deliveryRadius} km`
                                    : "—"}
                                </td>
                                <td>
                                  {entry.status === "QUALIFIED" ? (
                                    <Badge color="success">✅ Qualified</Badge>
                                  ) : (
                                    <Badge color="danger">❌ Rejected</Badge>
                                  )}
                                </td>
                                <td>
                                  {entry.reason ? (
                                    <div>
                                      <RejectionBadge reason={entry.reason} />
                                      {entry.reason === "CLOSED" &&
                                        entry.openingTime && (
                                          <div className="mt-1">
                                            <small className="text-muted">
                                              {entry.openingTime} –{" "}
                                              {entry.closingTime}
                                            </small>
                                          </div>
                                        )}
                                      {entry.reason === "OUTSIDE_RADIUS" &&
                                        entry.distance != null && (
                                          <div className="mt-1">
                                            <small className="text-muted">
                                              {entry.distance} km &gt;{" "}
                                              {entry.deliveryRadius} km radius
                                            </small>
                                          </div>
                                        )}
                                    </div>
                                  ) : (
                                    <span className="text-success fw-semibold">
                                      Nearest eligible
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Raw JSON */}
            <Row className="mb-4">
              <Col md={12}>
                <Card className="shadow-sm border-0">
                  <CardBody>
                    <div
                      className="d-flex justify-content-between align-items-center"
                      style={{ cursor: "pointer" }}
                      onClick={() => setShowRaw((v) => !v)}
                    >
                      <h6 className="text-uppercase text-muted fw-semibold mb-0">
                        <i className="mdi mdi-code-json me-2" />
                        Raw API Response
                      </h6>
                      <i
                        className={`mdi ${
                          showRaw ? "mdi-chevron-up" : "mdi-chevron-down"
                        }`}
                      />
                    </div>
                    {showRaw && (
                      <pre
                        className="mt-3 p-3 rounded"
                        style={{
                          background: "#1e1e1e",
                          color: "#d4d4d4",
                          fontSize: "0.78rem",
                          maxHeight: "400px",
                          overflowY: "auto",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-all",
                        }}
                      >
                        {JSON.stringify(result, null, 2)}
                      </pre>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </div>
  );
}
